<template>
  <transition name="broker-popup-fade">
    <div v-if="visible" class="broker-popup-overlay" @click.self="dismiss">
      <div class="broker-popup" :class="{ dark: isDark }">
        <button class="broker-popup-close" @click="dismiss" aria-label="Close">&times;</button>

        <!-- Token Expired - Reconnect -->
        <div v-if="mode === 'reconnect'" class="broker-popup-body">
          <div class="broker-popup-icon warning">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <h3 class="broker-popup-title">{{ expiredBrokerName }} Session Expired</h3>
          <p class="broker-popup-desc">
            Your {{ expiredBrokerName }} connection has expired. Reconnect to continue receiving real-time NSE/BSE data.
          </p>
          <div class="broker-popup-actions">
            <button class="broker-popup-btn primary" @click="reconnect" :disabled="reconnecting">
              <span v-if="reconnecting">Reconnecting...</span>
              <span v-else>Reconnect Now</span>
            </button>
            <button class="broker-popup-btn secondary" @click="dismiss" :disabled="reconnecting">
              Not Now
            </button>
          </div>
        </div>

        <!-- Reconnecting State -->
        <div v-else-if="mode === 'reconnecting'" class="broker-popup-body">
          <div class="broker-popup-icon loading">
            <div class="loading-spinner"></div>
          </div>
          <h3 class="broker-popup-title">Reconnecting to {{ expiredBrokerName }}</h3>
          <p class="broker-popup-desc">
            Please complete the login in the popup window to restore your connection...
          </p>
        </div>

        <!-- Success State -->
        <div v-else-if="mode === 'success'" class="broker-popup-body">
          <div class="broker-popup-icon success">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h3 class="broker-popup-title">Reconnected!</h3>
          <p class="broker-popup-desc">
            Your {{ expiredBrokerName }} connection has been restored. Real-time data will resume shortly.
          </p>
          <div class="broker-popup-actions">
            <button class="broker-popup-btn primary" @click="dismiss">Done</button>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="mode === 'error'" class="broker-popup-body">
          <div class="broker-popup-icon error">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3 class="broker-popup-title">Reconnection Failed</h3>
          <p class="broker-popup-desc">
            {{ errorMessage || 'Could not reconnect. Please try again or connect from the Dashboard.' }}
          </p>
          <div class="broker-popup-actions">
            <button class="broker-popup-btn primary" @click="reconnect">
              Try Again
            </button>
            <button class="broker-popup-btn secondary" @click="goToDashboard">
              Go to Dashboard
            </button>
          </div>
        </div>

        <!-- First Time - Connect -->
        <div v-else-if="mode === 'connect'" class="broker-popup-body">
          <div class="broker-popup-icon info">
            <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="12" fill="#FF6B35"/>
              <path d="M24 12C17.3726 12 12 17.3726 12 24C12 30.6274 17.3726 36 24 36C30.6274 36 36 30.6274 36 24C36 17.3726 30.6274 12 24 12Z" fill="white"/>
              <path d="M24 18C20.6863 18 18 20.6863 18 24C18 27.3137 20.6863 30 24 30C27.3137 30 30 27.3137 30 24C30 20.6863 27.3137 18 24 18Z" fill="#FF6B35"/>
            </svg>
          </div>
          <h3 class="broker-popup-title">Connect a Broker for Indian Markets</h3>
          <p class="broker-popup-desc">
            Get real-time NSE, BSE & F&O data with professional charting. Connect your broker account to unlock Indian market data.
          </p>
          <div class="broker-popup-actions">
            <button class="broker-popup-btn primary" @click="goToDashboard">
              Connect in Dashboard
            </button>
            <button class="broker-popup-btn secondary" @click="skipForever">
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import FyersService from '../services/fyers_service.js';
import UpstoxService from '../services/upstox_service.js';
import AliceBlueService from '../services/aliceblue_service.js';
import DhanService from '../services/dhan_service.js';
import TrueDataService from '../services/truedata_service.js';
import GlobalDataFeedsService from '../services/globaldatafeeds_service.js';
import DataProviderManager from '../services/data_provider_manager.js';
import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE || '';

// localStorage keys
const SKIP_CONNECT_KEY = 'broker_skip_connect_popup';
const SKIP_RECONNECT_KEY = 'broker_skip_reconnect_popup';

export default {
  name: 'BrokerReconnectPopup',
  props: {
    isDark: { type: Boolean, default: false }
  },
  data() {
    return {
      visible: false,
      mode: null, // 'connect' | 'reconnect' | 'reconnecting' | 'success' | 'error'
      reconnecting: false,
      expiredBrokerKey: null, // 'fyers' | 'upstox' | 'aliceblue' | 'dhan' | 'truedata' | 'globaldatafeeds'
      errorMessage: ''
    };
  },
  computed: {
    expiredBrokerName() {
      const names = { fyers: 'Fyers', upstox: 'Upstox', aliceblue: 'AliceBlue', dhan: 'DhanHQ', truedata: 'TrueData', globaldatafeeds: 'Global Data Feeds' };
      return names[this.expiredBrokerKey] || 'Broker';
    },
    expiredBrokerService() {
      const services = { fyers: FyersService, upstox: UpstoxService, aliceblue: AliceBlueService, dhan: DhanService, truedata: TrueDataService, globaldatafeeds: GlobalDataFeedsService };
      return services[this.expiredBrokerKey];
    }
  },
  mounted() {
    this.checkBrokerStatus();
  },
  methods: {
    async checkBrokerStatus() {
      try {
        // Check each broker in priority order
        const brokers = [
          { key: 'fyers', service: FyersService },
          { key: 'upstox', service: UpstoxService },
          { key: 'aliceblue', service: AliceBlueService },
          { key: 'dhan', service: DhanService },
          { key: 'truedata', service: TrueDataService },
          { key: 'globaldatafeeds', service: GlobalDataFeedsService }
        ];

        // If any broker is currently authenticated and valid, no popup needed
        for (const broker of brokers) {
          if (broker.service.isAuthenticated()) {
            const result = await broker.service.validateSession();
            if (result.valid) {
              return; // At least one broker is working fine
            }
            if (result.expired) {
              // This broker's token is expired — check if we have stored credentials
              const hasCredentials = await this.checkHasStoredCredentials(broker.key);
              if (hasCredentials) {
                // Notify parent that reconnect is needed (even if popup is dismissed)
                this.$emit('needs-reconnect', broker.key);
                // Show reconnect popup for this broker
                if (this.isReconnectDismissed()) return;
                this.expiredBrokerKey = broker.key;
                this.mode = 'reconnect';
                this.visible = true;
                return;
              }
            }
          }
        }

        // No broker is authenticated — check if user has ever connected
        const everConnected = await FyersService.hasEverConnected();
        if (!everConnected) {
          if (this.isConnectSkipped()) return;
          this.mode = 'connect';
          this.visible = true;
        }
      } catch (err) {
        console.warn('[BrokerReconnectPopup] check error:', err.message);
      }
    },

    async checkHasStoredCredentials(brokerKey) {
      try {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + `/api/broker-credentials/${brokerKey}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        return response.data.canConnect;
      } catch (e) {
        return false;
      }
    },

    isConnectSkipped() {
      return localStorage.getItem(SKIP_CONNECT_KEY) === 'true';
    },

    isReconnectDismissed() {
      return localStorage.getItem(SKIP_RECONNECT_KEY) === 'true';
    },

    /**
     * Auto-reconnect: uses stored credentials to initiate OAuth flow directly.
     * Opens the broker login popup, and after user completes login, restores the session.
     */
    async reconnect() {
      if (!this.expiredBrokerKey || !this.expiredBrokerService) {
        this.goToDashboard();
        return;
      }

      this.reconnecting = true;
      this.mode = 'reconnecting';
      this.errorMessage = '';

      try {
        // Clear the expired session
        this.expiredBrokerService.clearSession();

        // Get the OAuth URL (server uses stored/env credentials automatically)
        const authUrl = await this.expiredBrokerService.getAuthUrl();

        // Open OAuth popup
        const popup = window.open(authUrl, 'broker_reconnect', 'width=600,height=700,scrollbars=yes,resizable=yes');

        if (!popup) {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }

        // Poll for popup close and complete login
        const pollInterval = setInterval(async () => {
          try {
            if (!popup || popup.closed) {
              clearInterval(pollInterval);

              // Small delay to ensure callback has been processed
              await new Promise(resolve => setTimeout(resolve, 1500));

              try {
                const result = await this.expiredBrokerService.completeLogin();
                // Success!
                this.mode = 'success';
                this.reconnecting = false;
                DataProviderManager.switchProvider(this.expiredBrokerKey);
                this.$emit('reconnected', this.expiredBrokerKey);
                resetBrokerReconnectDismissal();

                // Auto-dismiss after 2 seconds
                setTimeout(() => { this.visible = false; }, 2000);
              } catch (completeErr) {
                this.errorMessage = completeErr.response?.data?.error || completeErr.message || 'Login was not completed.';
                this.mode = 'error';
                this.reconnecting = false;
              }
            }
          } catch (e) {
            // Cross-origin access error is expected while popup is on broker domain
          }
        }, 1000);

        // Safety timeout — stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (this.reconnecting) {
            this.reconnecting = false;
            this.errorMessage = 'Login timed out. Please try again.';
            this.mode = 'error';
          }
        }, 300000);

      } catch (error) {
        console.error(`[BrokerReconnectPopup] Reconnect failed:`, error);
        this.errorMessage = error.response?.data?.error || error.message || 'Reconnection failed.';
        this.mode = 'error';
        this.reconnecting = false;
      }
    },

    goToDashboard() {
      this.visible = false;
      this.$router.push('/dashboard/brokers');
    },

    skipForever() {
      localStorage.setItem(SKIP_CONNECT_KEY, 'true');
      this.visible = false;
    },

    dismiss() {
      if (this.mode === 'reconnect') {
        localStorage.setItem(SKIP_RECONNECT_KEY, 'true');
      }
      this.visible = false;
      this.reconnecting = false;
      this.mode = null;
    },

    resetDismissal() {
      localStorage.removeItem(SKIP_RECONNECT_KEY);
    },

    async _getClerkToken() {
      try {
        if (window.__vue_clerk && window.__vue_clerk.getToken) {
          return await window.__vue_clerk.getToken();
        }
        if (window.Clerk && window.Clerk.session) {
          return await window.Clerk.session.getToken();
        }
        if (window.Clerk && window.Clerk.getToken) {
          return await window.Clerk.getToken();
        }
      } catch (e) {
        return null;
      }
      return null;
    }
  }
};

// Export helper for external use
export function resetBrokerReconnectDismissal() {
  localStorage.removeItem(SKIP_RECONNECT_KEY);
}
</script>

<style scoped>
.broker-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
}

.broker-popup {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  position: relative;
  animation: popup-scale 0.2s ease-out;
}

.broker-popup.dark {
  background: #1e2433;
  color: #e1e4ea;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

@keyframes popup-scale {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.broker-popup-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.broker-popup-close:hover {
  color: #333;
  background: rgba(0, 0, 0, 0.05);
}

.broker-popup.dark .broker-popup-close:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.broker-popup-body {
  text-align: center;
}

.broker-popup-icon {
  margin-bottom: 16px;
}

.broker-popup-icon.warning svg {
  color: #f59e0b;
}

.broker-popup-icon.info {
  display: inline-block;
}

.broker-popup-icon.success svg {
  color: #22c55e;
}

.broker-popup-icon.error svg {
  color: #ef4444;
}

.broker-popup-icon.loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 107, 53, 0.2);
  border-top: 3px solid #FF6B35;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.broker-popup-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #1a1a2e;
}

.broker-popup.dark .broker-popup-title {
  color: #f0f0f5;
}

.broker-popup-desc {
  font-size: 14px;
  color: #666;
  margin: 0 0 24px;
  line-height: 1.5;
}

.broker-popup.dark .broker-popup-desc {
  color: #9ca3af;
}

.broker-popup-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.broker-popup-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
}

.broker-popup-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.broker-popup-btn.primary {
  background: #FF6B35;
  color: white;
}

.broker-popup-btn.primary:hover:not(:disabled) {
  background: #e55a28;
  transform: translateY(-1px);
}

.broker-popup-btn.secondary {
  background: transparent;
  color: #666;
  border: 1px solid #ddd;
}

.broker-popup-btn.secondary:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
}

.broker-popup.dark .broker-popup-btn.secondary {
  color: #9ca3af;
  border-color: #374151;
}

.broker-popup.dark .broker-popup-btn.secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
}

/* Transition */
.broker-popup-fade-enter-active,
.broker-popup-fade-leave-active {
  transition: opacity 0.2s ease;
}

.broker-popup-fade-enter,
.broker-popup-fade-leave-to {
  opacity: 0;
}
</style>
